import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Dashboard from './pages/Dashboard';
import QuoteBuilder from './pages/QuoteBuilder';
import MyQuotes from './pages/MyQuotes';
import Login from './pages/Login';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import Providers from './pages/admin/Providers';
import Regions from './pages/admin/Regions';
import VMTypes from './pages/admin/VMTypes';
import OSTypes from './pages/admin/OSTypes';
import Addons from './pages/admin/Addons';
import DefaultAddons from './pages/admin/DefaultAddons';
import Environments from './pages/admin/Environments';
import VMPricing from './pages/admin/pricing/VMPricing';
import AddonPricing from './pages/admin/pricing/AddonPricing';
import Disks from './pages/admin/Disks';
import DatabaseTypes from './pages/admin/DatabaseTypes';
import DatabaseConfigs from './pages/admin/DatabaseConfigs';
import AddonOptions from './pages/admin/AddonOptions';
import DiskPricing from './pages/admin/pricing/DiskPricing';
import DatabasePricing from './pages/admin/pricing/DatabasePricing';
import DatabaseVMPricing from './pages/admin/pricing/DatabaseVMPricing';
import AddonOptionPricing from './pages/admin/pricing/AddonOptionPricing';
import DatabaseEditions from './pages/admin/DatabaseEditions';
import Users from './pages/admin/Users';
import AuditLog from './pages/admin/AuditLog';
import APIMappings from './pages/admin/APIMappings';
import PriceImport from './pages/admin/PriceImport';
import PowerTypes from './pages/admin/PowerTypes';
import PowerAddons from './pages/admin/PowerAddons';
import PowerVMPricing from './pages/admin/pricing/PowerVMPricing';
import PowerAddonPricing from './pages/admin/pricing/PowerAddonPricing';
import PowerDefaultAddons from './pages/admin/PowerDefaultAddons';
import PowerAddonOptions from './pages/admin/PowerAddonOptions';
import PowerAddonOptionPricing from './pages/admin/pricing/PowerAddonOptionPricing';
import PowerDiskPricing from './pages/admin/pricing/PowerDiskPricing';
import BackupPricing from './pages/admin/pricing/BackupPricing';
import DatabaseBackupPricing from './pages/admin/pricing/DatabaseBackupPricing';
import DatabaseExtraStoragePricing from './pages/admin/pricing/DatabaseExtraStoragePricing';
import DRTestingPricing from './pages/admin/pricing/DRTestingPricing';

// Auth Route Guard
const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Route Guard
const RequireAdmin = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/quote-builder" element={<RequireAuth><QuoteBuilder /></RequireAuth>} />
            <Route path="/my-quotes" element={<RequireAuth><MyQuotes /></RequireAuth>} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/admin/providers" replace />} />
              <Route path="providers" element={<Providers />} />
              <Route path="regions" element={<Regions />} />
              <Route path="vm-types" element={<VMTypes />} />
              <Route path="os-types" element={<OSTypes />} />
              <Route path="addons" element={<Addons />} />
              <Route path="default-addons" element={<DefaultAddons />} />
              <Route path="environments" element={<Environments />} />
              <Route path="disks" element={<Disks />} />
              <Route path="database-types" element={<DatabaseTypes />} />
              <Route path="database-configs" element={<DatabaseConfigs />} />
              <Route path="database-editions" element={<DatabaseEditions />} />
              <Route path="addon-options" element={<AddonOptions />} />
              <Route path="pricing/vms" element={<VMPricing />} />
              <Route path="pricing/addons" element={<AddonPricing />} />
              <Route path="pricing/addon-options" element={<AddonOptionPricing />} />
              <Route path="pricing/disks" element={<DiskPricing />} />
              <Route path="pricing/databases" element={<DatabasePricing />} />
              <Route path="pricing/database-vms" element={<DatabaseVMPricing />} />
              <Route path="users" element={<Users />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="api-mappings" element={<APIMappings />} />
              <Route path="pricing/import" element={<PriceImport />} />
              <Route path="power-types" element={<PowerTypes />} />
              <Route path="power-addons" element={<PowerAddons />} />
              <Route path="pricing/power-vms" element={<PowerVMPricing />} />
              <Route path="pricing/power-disks" element={<PowerDiskPricing />} />
              <Route path="pricing/power-addons" element={<PowerAddonPricing />} />
              <Route path="power-addon-options" element={<PowerAddonOptions />} />
              <Route path="pricing/power-addon-options" element={<PowerAddonOptionPricing />} />
              <Route path="pricing/backup-vms" element={<BackupPricing />} />
              <Route path="pricing/backup-databases" element={<DatabaseBackupPricing />} />
              <Route path="pricing/extra-storage" element={<DatabaseExtraStoragePricing />} />
              <Route path="pricing/dr-testing" element={<DRTestingPricing />} />
              <Route path="power-default-addons" element={<PowerDefaultAddons />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
